import classNames from 'classnames';
import type { CSSProperties, ComponentProps, ReactNode } from 'react';
import styled from 'styled-components';

import { HexagonShape } from 'atoms/icons/HexagonShape';
import { Horizontal } from 'atoms/layout/flex';
import { absCenter, unitMapping } from 'lib/style';
import type { Color } from 'style/types';

const Wrapper = styled(Horizontal).attrs({ gap: 0, center: true })`
  position: relative;
  width: 32px;
  height: 32px;
  isolation: isolate;
`;

const Score = styled(Horizontal).attrs({ gap: 0, center: true })`
  ${absCenter}
  font-weight: var(--t-bold);
  font-size: var(--fs-14);
  font-family: var(--brand-font);
  font-style: normal;
  letter-spacing: -0.03em;
  z-index: 1;
  color: var(--default-color);
  &.inverted {
    color: var(--inverted-color);
  }
  &.invalid {
    color: var(--c-onBase-error);
  }
  &.small {
    font-size: 12px;
  }
`;

type Props = {
  score: ReactNode;
  invalid?: boolean;
  title?: string;
  className?: string;
  style?: CSSProperties;
  color?: Color;
} & Pick<ComponentProps<typeof HexagonShape>, 'size' | 'inverted' | 'noBorder'>;
export const Cost = ({
  score,
  invalid = false,
  title,
  className,
  style,
  color,
  size,
  inverted,
  noBorder,
}: Props) => {
  const roundedScore = typeof score === 'number' ? Math.round(score) : score;

  return (
    <Wrapper
      aria-label="cost"
      className={className}
      style={{
        ...style,
        ...(size
          ? { width: unitMapping[size], height: unitMapping[size] }
          : {}),
      }}
      title={title}
    >
      <Score
        style={
          {
            '--default-color': color ? 'var(--c-black)' : 'var(--c-white)',
            '--inverted-color': color,
            '--invalid-color': 'var(--c-rare-tertiary)',
          } as CSSProperties
        }
        className={classNames({
          inverted,
          invalid,
          small: size && size <= 3,
        })}
      >
        {roundedScore}
      </Score>
      <HexagonShape
        fill={invalid ? 'var(--c-rare-tertiary)' : color}
        size={size}
        inverted={inverted}
        noBorder={noBorder}
      />
    </Wrapper>
  );
};
